import { forwardRef, Ref, useMemo } from "react";
import { computeConnectorDrawingData } from "./connector-drawing-utils";
import { ConnectorLine } from "./connector-line";
import Konva from "konva";
import useConnectorShapes from "frontend/hooks/use-connector-shapes";
import { useDeepEqualMemo } from "frontend/hooks/use-deep-memoize";
import { Group } from "react-konva";
import normalAt from "frontend/geometry/normalAt";
import * as utils from "./connector-utils";
import { getElementTypeForId } from "../canvas-elements-utils";
import { Degrees } from "utils/transform";
import { parseStrokeWidth } from "shared/util/utils";
import { Sizes } from "frontend/data/shapes/sizes";

enum AnchorIndex {
  Start,
  End,
}

const GhostConnector = forwardRef(
  (
    {
      id,
      p1,
      p2,
      lineType,
      element,
      curveStrength,
    }: {
      id: string;
      p1: utils.ConnectorEndpoint;
      p2: utils.ConnectorEndpoint;
      lineType: "line" | "curve" | "elbow";
      element: any;
      curveStrength?: number;
    },
    ref: Ref<Konva.Shape>
  ) => {
    const connectedShapes = element.connectedShapes ?? [null, null];
    const { startShape, endShape, currentElement, getShape } = useConnectorShapes(id, element);

    function calcAnchorPosition(index: AnchorIndex): utils.ConnectorEndpoint {
      const point = [p1, p2][index];
      const elementPoint = utils.canvasPointToElementPoint(element, point);
      const rotation = point.rotation;
      const result = { ...elementPoint, rotation };
      if (!connectedShapes[index]?.id) return result;

      const mode = utils.fixAnchorsMode(element.anchorsMode?.[index]);
      if (mode === "outline") {
        const shape = getShape(index);
        if (!shape) return result;
        const t = index == 0 ? element.point1_t : element.point2_t;
        const elementType = getElementTypeForId(connectedShapes[index]!.id);
        const rotation = normalAt(elementType, shape)(t);
        result.rotation = rotation as Degrees;
      }
      return result;
    }

    const elementP1 = calcAnchorPosition(AnchorIndex.Start);
    const elementP2 = calcAnchorPosition(AnchorIndex.End);

    const startBbox = useDeepEqualMemo(
      utils.getConnectedShapeBbox(currentElement, elementP1, connectedShapes[0]?.id, startShape)
    );
    const endBbox = useDeepEqualMemo(
      utils.getConnectedShapeBbox(currentElement, elementP2, connectedShapes[1]?.id, endShape)
    );

    const startRotate = startShape?.rotate ?? 0;
    const endRotate = endShape?.rotate ?? 0;
    const memoizedElement = useDeepEqualMemo({
      lineType,
      innerPoints: element.innerPoints,
      strokeWidth: parseStrokeWidth(element.strokeWidth ?? Sizes.get().connector.strokeWidth.default),
      scaleX: element.scaleX,
      scaleY: element.scaleY,
      rotate: element.rotate,
      anchorsMode: element.anchorsMode,
      pointerStyles: element.pointerStyles,
    });

    const data = useMemo(() => {
      if (!memoizedElement.lineType) return null;
      if (isNaN(elementP1.x) || isNaN(elementP2.x)) return null;
      return computeConnectorDrawingData(
        elementP1,
        elementP2,
        memoizedElement,
        startBbox,
        endBbox,
        startRotate,
        endRotate,
        curveStrength
      );
    }, [
      elementP1.x,
      elementP1.y,
      elementP2.x,
      elementP2.y,
      startBbox,
      endBbox,
      memoizedElement,
      startRotate,
      endRotate,
      curveStrength,
    ]);

    if (!data) return null;
    return (
      <Group x={element.x} y={element.y}>
        <ConnectorLine p1={elementP1} p2={elementP2} id={id} element={element} data={data} ref={ref} />
      </Group>
    );
  }
);

GhostConnector.displayName = "GhostConnector";
export default GhostConnector;
